// W2Cook Billing Dashboard — API Connector Templates
// Each template defines how to reach a service's billing/usage API.
// Credentials are stored separately (Firestore in prod, localStorage in dev).
// To add a new connector: append to BUILTIN_CONNECTOR_TEMPLATES.

export interface ConnectorField {
  key: string;
  label: string;
  placeholder: string;
  secret: boolean;           // rendered as password input
}

export interface LiveResult {
  ok: boolean;
  amount: number | null;     // if the API returns a parseable cost
  note: string;
  fetchedAt: string;         // ISO timestamp
  raw?: unknown;
}

export interface ConnectorTemplate {
  id: string;
  name: string;
  icon: string;
  docsUrl: string;
  fields: ConnectorField[];
  /**
   * True when the vendor sends CORS headers, so the browser can call it
   * directly. Verified by inspecting access-control-allow-origin on a live
   * request. Everything else must be relayed via the Cloud Run /proxy route.
   */
  corsOk?: boolean;
  _custom?: boolean;
  // These are functions so credentials are never embedded in templates
  buildEndpoint: (cfg: Record<string, string>) => string;
  buildHeaders:  (cfg: Record<string, string>) => Record<string, string>;
  parseResult:   (data: unknown, cfg: Record<string, string>) => Omit<LiveResult, "fetchedAt">;
}

export const BUILTIN_CONNECTOR_TEMPLATES: ConnectorTemplate[] = [
  {
    id: "gcp",
    name: "GCP / Firebase Billing",
    icon: "🔥",
    docsUrl: "https://cloud.google.com/billing/docs/apis",
    fields: [
      { key: "billingAccountId", label: "Billing Account ID",   placeholder: "XXXXXX-XXXXXX-XXXXXX", secret: false },
      { key: "accessToken",      label: "Access Token (Bearer)", placeholder: "ya29.xxx",              secret: true  },
    ],
    buildEndpoint: (cfg) =>
      `https://cloudbilling.googleapis.com/v1/billingAccounts/${cfg.billingAccountId}/budgets`,
    buildHeaders: (cfg) => ({ Authorization: `Bearer ${cfg.accessToken}` }),
    parseResult: (data) => ({
      ok: true,
      amount: null,
      note: "GCP Billing API reached — check Cloud Console for actuals",
      raw: data,
    }),
  },
  {
    id: "github",
    corsOk: true,
    name: "GitHub Billing",
    icon: "\u{1F419}",
    docsUrl: "https://docs.github.com/en/rest/billing/usage",
    fields: [
      { key: "org", label: "Org Name",              placeholder: "dhivinslabs-app", secret: false },
      { key: "pat", label: "Personal Access Token", placeholder: "ghp_xxx",         secret: true  },
    ],
    // Enhanced billing platform endpoint. The old
    // /orgs/{org}/settings/billing/actions route now returns HTTP 410.
    // Note: /organizations/ (plural, full word), not /orgs/.
    buildEndpoint: (cfg) =>
      `https://api.github.com/organizations/${cfg.org}/settings/billing/usage`,
    buildHeaders: (cfg) => ({
      Authorization: `Bearer ${cfg.pat}`,
      Accept: "application/vnd.github+json",
      "X-GitHub-Api-Version": "2022-11-28",
    }),
    parseResult: (data: any) => {
      const items: any[] = Array.isArray(data?.usageItems) ? data.usageItems : [];
      if (items.length === 0) {
        return { ok: true, amount: 0, note: "GitHub: no billable usage this period", raw: data };
      }
      const net = items.reduce((sum, i) => sum + (Number(i?.netAmount) || 0), 0);
      const products = Array.from(new Set(items.map(i => i?.product).filter(Boolean)));
      return {
        ok: true,
        amount: net,
        note: `GitHub: $${net.toFixed(2)} across ${products.join(", ") || "usage"}`,
        raw: data,
      };
    },
  },
  {
    id: "anthropic",
    name: "Anthropic API Usage",
    icon: "🤖",
    docsUrl: "https://docs.anthropic.com/en/api/usage",
    fields: [
      { key: "apiKey", label: "API Key", placeholder: "sk-ant-xxx", secret: true },
    ],
    buildEndpoint: () => `https://api.anthropic.com/v1/usage`,
    buildHeaders: (cfg) => ({
      "x-api-key": cfg.apiKey,
      "anthropic-version": "2023-06-01",
    }),
    parseResult: (data) => ({
      ok: true,
      amount: null,
      note: "Anthropic usage data fetched — see Console for token spend",
      raw: data,
    }),
  },
  {
    id: "sentry",
    name: "Sentry Usage",
    icon: "🛡️",
    docsUrl: "https://docs.sentry.io/api/",
    fields: [
      { key: "authToken", label: "Auth Token", placeholder: "sntrys_xxx",    secret: true  },
      { key: "orgSlug",   label: "Org Slug",   placeholder: "dhivinslabs",   secret: false },
    ],
    buildEndpoint: (cfg) =>
      `https://sentry.io/api/0/organizations/${cfg.orgSlug}/stats_v2/?field=sum(quantity)&category=error&interval=1d`,
    buildHeaders: (cfg) => ({ Authorization: `Bearer ${cfg.authToken}` }),
    parseResult: (data) => ({
      ok: true,
      amount: null,
      note: "Sentry org stats fetched",
      raw: data,
    }),
  },
  {
    id: "revenuecat",
    name: "RevenueCat",
    icon: "💰",
    docsUrl: "https://www.revenuecat.com/docs/api-v1",
    fields: [
      { key: "secretKey", label: "Secret API Key", placeholder: "sk_xxx", secret: true },
    ],
    buildEndpoint: () => `https://api.revenuecat.com/v1/subscribers/overview`,
    buildHeaders: (cfg) => ({ Authorization: `Bearer ${cfg.secretKey}` }),
    parseResult: (data: any) => ({
      ok: true,
      amount: null,
      note: `RevenueCat: MRR $${data?.subscriber_count ?? "?"} subscribers`,
      raw: data,
    }),
  },
  {
    id: "cloudflare",
    name: "Cloudflare Billing",
    icon: "☁️",
    docsUrl: "https://developers.cloudflare.com/api/",
    fields: [
      { key: "accountId", label: "Account ID", placeholder: "abc123", secret: false },
      { key: "apiToken",  label: "API Token",  placeholder: "xxx",    secret: true  },
    ],
    buildEndpoint: (cfg) =>
      `https://api.cloudflare.com/client/v4/accounts/${cfg.accountId}/billing/profile`,
    buildHeaders: (cfg) => ({ Authorization: `Bearer ${cfg.apiToken}` }),
    parseResult: (data: any) => ({
      ok: !!data?.success,
      amount: data?.result?.balance ?? null,
      note: `Cloudflare balance: $${data?.result?.balance ?? "n/a"}`,
      raw: data,
    }),
  },
  {
    id: "terraform",
    name: "HCP Terraform",
    icon: "🏗️",
    docsUrl: "https://developer.hashicorp.com/terraform/cloud-docs/api-docs",
    fields: [
      { key: "orgName", label: "Org Name",   placeholder: "dhivinslabs", secret: false },
      { key: "token",   label: "Team Token", placeholder: "xxx.atlasv1.xxx", secret: true },
    ],
    buildEndpoint: (cfg) =>
      `https://app.terraform.io/api/v2/organizations/${cfg.orgName}/entitlement-set`,
    buildHeaders: (cfg) => ({
      Authorization: `Bearer ${cfg.token}`,
      "Content-Type": "application/vnd.api+json",
    }),
    parseResult: (data) => ({
      ok: true,
      amount: null,
      note: "HCP Terraform entitlements fetched",
      raw: data,
    }),
  },
  {
    id: "zoho",
    name: "Zoho Billing",
    icon: "📧",
    docsUrl: "https://www.zoho.com/subscriptions/api/v1/",
    fields: [
      { key: "orgId",     label: "Org ID",       placeholder: "xxx",       secret: false },
      { key: "authToken", label: "OAuth Token",   placeholder: "1000.xxx",  secret: true  },
    ],
    buildEndpoint: (cfg) =>
      `https://subscriptions.zoho.com/api/v1/subscriptions?organization_id=${cfg.orgId}`,
    buildHeaders: (cfg) => ({
      Authorization: `Zoho-oauthtoken ${cfg.authToken}`,
    }),
    parseResult: (data) => ({
      ok: true,
      amount: null,
      note: "Zoho subscriptions fetched",
      raw: data,
    }),
  },
  {
    id: "figma",
    corsOk: true,
    name: "Figma",
    icon: "🎨",
    docsUrl: "https://www.figma.com/developers/api",
    fields: [
      { key: "pat", label: "Personal Access Token", placeholder: "figd_xxx", secret: true },
    ],
    buildEndpoint: () => `https://api.figma.com/v1/me`,
    buildHeaders: (cfg) => ({ "X-Figma-Token": cfg.pat }),
    parseResult: (data: any) => ({
      ok: !!data?.id,
      amount: null,
      note: `Figma: ${data?.email ?? "connected"} — plan not exposed via API`,
      raw: data,
    }),
  },
];
